<template>
  <main class="leitner">
    <div class="leitner__inner">
      <button type="button" class="leitner__back" aria-label="بازگشت" @click="goBack">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M5 12H19" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M13 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span>بازگشت</span>
      </button>

      <header class="leitner__header">
        <p class="leitner__eyebrow">جعبه لایتنر</p>
        <h1 class="leitner__title">تمرین واژگان با مرور فاصله‌دار</h1>
        <p class="leitner__subtitle">
          واژه‌هات رو با دکمه شناور «لایتنر» اضافه کن. درست‌ها به جعبه بعدی می‌روند؛ اشتباه‌ها به جعبه ۱ برمی‌گردند.
        </p>
      </header>

      <LoadingSpinner v-if="loading" message="در حال آماده‌سازی جعبه‌ها..." />

      <ErrorMessage
        v-else-if="errorMessage"
        :message="errorMessage"
        @retry="load"
      />

      <template v-else-if="overview">
        <section class="leitner__stats" aria-label="خلاصه پیشرفت">
          <div class="leitner__stat">
            <strong>{{ toPersianDigits(overview.due_count) }}</strong>
            <span>آماده مرور</span>
          </div>
          <div class="leitner__stat">
            <strong>{{ toPersianDigits(overview.total_cards) }}</strong>
            <span>کل کارت‌ها</span>
          </div>
          <div class="leitner__stat">
            <strong>{{ toPersianDigits(overview.mastered_count) }}</strong>
            <span>جعبه ۵</span>
          </div>
        </section>

        <section class="leitner__boxes" aria-label="جعبه‌های لایتنر">
          <div
            v-for="box in overview.boxes"
            :key="box.box"
            class="leitner__box"
            :class="`leitner__box--${box.box}`"
          >
            <span class="leitner__box-label">جعبه {{ toPersianDigits(box.box) }}</span>
            <strong class="leitner__box-count">{{ toPersianDigits(box.count) }}</strong>
            <span class="leitner__box-hint">{{ boxHint(box.box) }}</span>
            <div class="leitner__box-bar" aria-hidden="true">
              <span :style="{ width: boxBarWidth(box.count) }"></span>
            </div>
          </div>
        </section>

        <div class="leitner__actions">
          <button
            type="button"
            class="leitner__cta"
            :disabled="overview.due_count === 0 || starting"
            @click="startReview"
          >
            {{ overview.due_count > 0 ? "شروع مرور امروز" : "فعلاً کارتی برای مرور نیست" }}
          </button>
          <p class="leitner__tip">
            هنوز واژه‌ای نداری؟ از دکمه شناور «لایتنر» در گوشه صفحه، کارت جدید بساز.
            فاصله مرور: جعبه ۱ → روزانه، ۲ → ۲ روز، ۳ → ۴ روز، ۴ → ۷ روز، ۵ → ۱۴ روز
          </p>
        </div>
      </template>
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { getLeitnerOverview } from "@/api/leitner";
import { handleError } from "@/services/errorHandler";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import ErrorMessage from "@/components/common/ErrorMessage.vue";
import type { LeitnerOverview } from "@/types";

const router = useRouter();
const overview = ref<LeitnerOverview | null>(null);
const loading = ref(true);
const starting = ref(false);
const errorMessage = ref<string | null>(null);

const maxBoxCount = computed(() =>
  Math.max(1, ...(overview.value?.boxes.map((b) => b.count) || [1]))
);

function toPersianDigits(value: number | string): string {
  return String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);
}

function boxHint(box: number): string {
  const hints: Record<number, string> = {
    1: "هر روز",
    2: "هر ۲ روز",
    3: "هر ۴ روز",
    4: "هر ۷ روز",
    5: "هر ۱۴ روز",
  };
  return hints[box] || "";
}

function boxBarWidth(count: number): string {
  return `${Math.round((count / maxBoxCount.value) * 100)}%`;
}

async function load() {
  loading.value = true;
  errorMessage.value = null;
  try {
    overview.value = await getLeitnerOverview();
  } catch (e) {
    errorMessage.value = handleError(e, "LeitnerBox.load");
  } finally {
    loading.value = false;
  }
}

async function startReview() {
  starting.value = true;
  try {
    await router.push("/dashboard/leitner/review");
  } finally {
    starting.value = false;
  }
}

function goBack() {
  if (window.history.state && window.history.state.back) {
    router.back();
  } else {
    router.push("/dashboard");
  }
}

onMounted(load);
</script>

<style scoped>
.leitner {
  --color-primary: #6D5EF8;
  --color-secondary: #6A4328;
  --color-bg: #FFFDF8;
  --color-card: #FFFFFF;
  --color-border: #ECE4D9;
  --color-text: #4C372A;
  --color-muted: #8C8176;

  min-height: 100vh;
  background: var(--color-bg);
  color: var(--color-text);
  font-family: Vazirmatn, "Segoe UI", Tahoma, sans-serif;
  padding: 56px 16px 80px;
  direction: rtl;
  box-sizing: border-box;
}

.leitner__inner {
  max-width: 840px;
  margin-inline: auto;
}

.leitner__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--color-card);
  border: 1px solid var(--color-border);
  color: var(--color-text);
  font-size: 14px;
  font-weight: 500;
  padding: 9px 18px;
  border-radius: 999px;
  cursor: pointer;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(76, 55, 42, 0.06);
}

.leitner__back:hover {
  background: #f5f3ff;
  border-color: var(--color-primary);
}

.leitner__eyebrow {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-secondary);
}

.leitner__title {
  margin: 0 0 10px;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.3;
}

.leitner__subtitle {
  margin: 0 0 28px;
  color: var(--color-muted);
  font-size: 15px;
  line-height: 1.7;
}

.leitner__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 22px;
}

.leitner__stat {
  background: var(--color-card);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 16px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.leitner__stat strong {
  font-size: 22px;
  color: var(--color-primary);
}

.leitner__stat span {
  font-size: 12px;
  color: var(--color-muted);
}

.leitner__boxes {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 28px;
}

.leitner__box {
  background: var(--color-card);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.leitner__box-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-muted);
}

.leitner__box-count {
  font-size: 22px;
  font-weight: 700;
  color: var(--color-text);
}

.leitner__box-hint {
  font-size: 10px;
  color: var(--color-muted);
}

.leitner__box-bar {
  height: 4px;
  border-radius: 999px;
  background: #F0EBE3;
  overflow: hidden;
  margin-top: 4px;
}

.leitner__box-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--color-primary);
}

.leitner__box--1 .leitner__box-bar span { background: #F59E0B; }
.leitner__box--2 .leitner__box-bar span { background: #E8A317; }
.leitner__box--3 .leitner__box-bar span { background: #7C4DFF; }
.leitner__box--4 .leitner__box-bar span { background: #6D5EF8; }
.leitner__box--5 .leitner__box-bar span { background: #79C26D; }

.leitner__actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.leitner__cta {
  border: none;
  border-radius: 14px;
  background: var(--color-primary);
  color: #fff;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  padding: 14px 18px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(109, 94, 248, 0.25);
}

.leitner__cta:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.leitner__cta:not(:disabled):hover {
  filter: brightness(1.04);
}

.leitner__tip {
  margin: 0;
  text-align: center;
  font-size: 12px;
  color: var(--color-muted);
  line-height: 1.6;
}

@media (max-width: 767.98px) {
  .leitner__boxes {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .leitner__title {
    font-size: 26px;
  }
}
</style>
